import axios from 'axios'
import { isEmpty } from 'lodash'

const api = axios.create({
  baseURL: '/tracer',
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
  },
})

const TERM_LOCAL_STORAGE_KEY = 'terminology'
/* we'll store terminology in local storage like:
{
  <make_model_id1>: {
     <language1>: {
      <code1>: <translation1>,
      ...
     },
     ...
  },
  "vin_map": {
    <vin1>: <make_model_id1>,
    ...
  }
} 
*/

const loadAllTerminologyInLocalStorage = () => {
  let res = {}
  const rawTerminology = localStorage.getItem(TERM_LOCAL_STORAGE_KEY)
  if (rawTerminology) {
    res = JSON.parse(rawTerminology)
  }
  return res
}

const saveTerminologyToLocalStorage = ({
  make_model_id,
  vin,
  language,
  terminology,
}) => {
  // the terminology object should be what the two apiGet* functions
  // format it into - an object keyed on code
  const allTerminology = loadAllTerminologyInLocalStorage()
  let make_model_terms = allTerminology[make_model_id]
  if (!make_model_terms) {
    make_model_terms = {}
    make_model_terms[language] = {}
  }
  for (const [code, result] of Object.entries(terminology)) {
    make_model_terms[language][code] = result
  }
  if (isEmpty(allTerminology['vin_map'])) {
    allTerminology['vin_map'] = {}
  }
  if (vin) {
    allTerminology['vin_map'][vin] = make_model_id
  }
  allTerminology[make_model_id] = make_model_terms
  const allTerminologyStr = JSON.stringify(allTerminology)
  localStorage.setItem(TERM_LOCAL_STORAGE_KEY, allTerminologyStr)
}

const loadTerminologyFromLocalStorage = ({
  make_model_id,
  vin,
  language,
  codes,
}) => {
  // returns an object like:
  // { 'terminology': <results formatted like the apiGet* functions>,
  //   'missingCodes': <the codes that did not have results in localStorage> }
  const allTerminology = loadAllTerminologyInLocalStorage()
  let result = {
    terminology: {},
    missingCodes: codes,
  }
  let searchMakeModelId = make_model_id
  if (!searchMakeModelId) {
    if (!!vin) {
      const vin_map = allTerminology['vin_map']
      if (vin_map) {
        searchMakeModelId = allTerminology['vin_map'][vin]
      }
      if (!searchMakeModelId) {
        return result
      }
    } else {
      return result
    }
  }
  const makeModelTerms = allTerminology[searchMakeModelId]
  if (isEmpty(makeModelTerms) || isEmpty(makeModelTerms[language])) {
    return result
  }
  const terms = makeModelTerms[language]
  result['missingCodes'] = []
  codes.forEach((code) => {
    if (terms.hasOwnProperty(code)) {
      result['terminology'][code] = terms[code]
    } else {
      result['missingCodes'].push(code)
    }
  })
  return result
}

export function apiGetTerminology({ makeModelId, vin, language, codes }) {
  const cachedTerms = loadTerminologyFromLocalStorage({
    make_model_id: makeModelId,
    vin,
    language,
    codes,
  })
  if (!isEmpty(cachedTerms['missingCodes'])) {
    let params
    if (!!makeModelId) {
      params = new URLSearchParams({
        codes,
        language,
        make_model_id: makeModelId,
      })
    } else if (!!vin) {
      params = new URLSearchParams({ codes, language, vin })
    }
    return api
      .get(`/terminology?${params.toString()}`)
      .then((resp) => {
        const terms = resp.data['terminology']
        const result = {}
        const resultMakeModelId = resp.data['make_model_id']
        terms.forEach((term) => {
          result[term['code']] = term
        })
        saveTerminologyToLocalStorage({
          make_model_id: resultMakeModelId,
          terminology: result,
          vin,
          language,
        })
        return result
      })
      .catch((e) => {
        return {}
      })
  } else {
    return Promise.resolve(cachedTerms['terminology'])
  }
}

export function apiGetTerminologyBatch(data) {
  const dataToSearch = []
  let termsAlreadyFetched = {}
  data.forEach((termReq) => {
    const cacheResult = loadTerminologyFromLocalStorage({
      make_model_id: termReq.make_model_id,
      vin: termReq.vin,
      language: termReq.language,
      codes: termReq.codes,
    })
    if (isEmpty(cacheResult['missingCodes'])) {
      termsAlreadyFetched = {
        ...termsAlreadyFetched,
        ...cacheResult['terminology'],
      }
    } else {
      dataToSearch.push(termReq)
    }
  })
  if (!isEmpty(dataToSearch)) {
    return api
      .post('/terminology_batch', data)
      .then((resp) => {
        const allTerms = resp.data
        let result = {}
        allTerms.forEach((term) => {
          let tmpRes = {}
          const terms = term['terminology']
          terms.forEach((trm) => {
            tmpRes[trm['code']] = trm
          })
          saveTerminologyToLocalStorage({
            make_model_id: term['make_model_id'],
            vin: term['vin'],
            language: term['language'],
            terminology: tmpRes,
          })
          result = { ...result, ...tmpRes }
        })
        return { ...result, ...termsAlreadyFetched }
      })
      .catch((e) => {
        return {}
      })
  } else {
    return Promise.resolve(termsAlreadyFetched)
  }
}
